import {
  runDeletePublishedFileWorkflow,
  runRenameFileRecordWorkflow,
} from "@/action-dialog";
import {
  useCanvasFileRecords,
  useCopyFileRecord,
  useDeleteFileRecord,
  useFlowSessionFileRecords,
  useWorkspaceFileRecords,
} from "@/api/fileRecords";
import { generateUniqueName } from "@/components/FileConflictResolver";
import type { FileRecordActionHandlerDeps } from "@/object-actions/handlers/fileRecord";
import { cascadeDeleteFolder } from "@/pages/workspaces/utils/folderDelete";
import { useOpenFileRecord } from "@/panes/file-record-detail/useOpenFileRecord";
import type { FileRecord } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";

function splitParentAndLeaf(path: string): { parent: string; leaf: string } {
  const cleaned = path.endsWith("/") ? path.slice(0, -1) : path;
  const lastSlash = cleaned.lastIndexOf("/");
  if (lastSlash === -1) return { parent: "", leaf: cleaned };
  return {
    parent: cleaned.slice(0, lastSlash + 1),
    leaf: cleaned.slice(lastSlash + 1),
  };
}

function getSiblingLeafNames(
  records: FileRecord[],
  parent: string,
): Set<string> {
  const names = new Set<string>();
  for (const r of records) {
    if (!r.path.startsWith(parent)) continue;
    const remainder = r.path.slice(parent.length);
    if (!remainder) continue;
    const cleaned = remainder.endsWith("/")
      ? remainder.slice(0, -1)
      : remainder;
    if (cleaned.length > 0 && !cleaned.includes("/")) names.add(cleaned);
  }
  return names;
}

function useFileRecordSiblingList(record: FileRecord): FileRecord[] {
  const workspaceQuery = useWorkspaceFileRecords(
    record.class === "workspace" ? record.workspace_id : undefined,
  );
  const canvasQuery = useCanvasFileRecords(
    record.class === "canvas" ? record.canvas_id : undefined,
  );
  const flowSessionQuery = useFlowSessionFileRecords(
    record.class === "flow-session" ? record.flow_session_id : undefined,
  );

  return useMemo(() => {
    switch (record.class) {
      case "workspace":
        return workspaceQuery.data ?? [];
      case "canvas":
        return canvasQuery.data ?? [];
      case "flow-session":
        return flowSessionQuery.data ?? [];
      default:
        return [];
    }
  }, [
    canvasQuery.data,
    flowSessionQuery.data,
    record.class,
    workspaceQuery.data,
  ]);
}

/**
 * Handler deps for the pane-header kebab, mirroring left-rail file row menus.
 */
export function useFileRecordPaneHeaderKebabHandlerDeps(
  record: FileRecord,
): FileRecordActionHandlerDeps {
  const queryClient = useQueryClient();
  const openFileRecord = useOpenFileRecord();
  const allRecords = useFileRecordSiblingList(record);
  const workspaceId = record.workspace_id ?? undefined;

  const deleteFileRecord = useDeleteFileRecord(workspaceId, {
    onError: () => toast.error("Failed to delete"),
  });
  const copyFileRecord = useCopyFileRecord(workspaceId, {
    onSuccess: () => toast.success("Duplicated"),
    onError: () => toast.error("Failed to duplicate"),
  });

  const handleDelete = useCallback(
    (target: FileRecord) => {
      if (target.path.endsWith("/")) {
        void cascadeDeleteFolder(
          target,
          allRecords,
          queryClient,
          workspaceId,
        ).catch(() => {
          toast.error("Failed to delete folder");
        });
        return;
      }
      void runDeletePublishedFileWorkflow({
        record: target,
        perform: () =>
          new Promise<void>((resolve, reject) => {
            deleteFileRecord.mutate(target.id, {
              onSuccess: () => resolve(),
              onError: () => reject(new Error("delete failed")),
            });
          }),
      });
    },
    [allRecords, deleteFileRecord, queryClient, workspaceId],
  );

  const handleCopy = useCallback(
    (target: FileRecord) => {
      const { parent, leaf } = splitParentAndLeaf(target.path);
      const isFolder = target.path.endsWith("/");
      const taken = getSiblingLeafNames(allRecords, parent);
      const uniqueLeaf = generateUniqueName(leaf, isFolder, taken);
      const newPath = `${parent}${uniqueLeaf}${isFolder ? "/" : ""}`;

      copyFileRecord.mutate({
        fileRecordId: target.id,
        payload: {
          destination: {
            class: target.class,
            workspace_id: target.workspace_id,
            canvas_id: target.canvas_id,
            flow_session_id: target.flow_session_id,
            path: newPath,
          },
        },
      });
    },
    [allRecords, copyFileRecord],
  );

  const handleRename = useCallback(
    (target: FileRecord) => {
      window.setTimeout(
        () => void runRenameFileRecordWorkflow({ record: target, allRecords }),
        0,
      );
    },
    [allRecords],
  );

  return useMemo(
    (): FileRecordActionHandlerDeps => ({
      onOpen: (target) => openFileRecord(target.id),
      onRename: handleRename,
      onCopy: handleCopy,
      onDelete: handleDelete,
    }),
    [handleCopy, handleDelete, handleRename, openFileRecord],
  );
}
